/*
 * Copyright (C) 2024 Das Land Schleswig-Holstein vertreten durch den
 * Ministerpräsidenten des Landes Schleswig-Holstein
 * Staatskanzlei
 * Abteilung Digitalisierung und zentrales IT-Management der Landesregierung
 *
 * Lizenziert unter der EUPL, Version 1.2 oder - sobald
 * diese von der Europäischen Kommission genehmigt wurden -
 * Folgeversionen der EUPL ("Lizenz");
 * Sie dürfen dieses Werk ausschließlich gemäß
 * dieser Lizenz nutzen.
 * Eine Kopie der Lizenz finden Sie hier:
 *
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 *
 * Sofern nicht durch anwendbare Rechtsvorschriften
 * gefordert oder in schriftlicher Form vereinbart, wird
 * die unter der Lizenz verbreitete Software "so wie sie
 * ist", OHNE JEGLICHE GEWÄHRLEISTUNG ODER BEDINGUNGEN -
 * ausdrücklich oder stillschweigend - verbreitet.
 * Die sprachspezifischen Genehmigungen und Beschränkungen
 * unter der Lizenz sind dem Lizenztext zu entnehmen.
 */
import { EMPTY_STRING } from '@common/tech-shared';
import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { TextInputComponent } from '../../form/text-input/text-input.component';
import { IconComponent } from '../../icon/icon.component';

@Component({
  selector: 'ods-search-field',
  imports: [TextInputComponent, IconComponent],
  template: `<ods-text-input
    [label]="label"
    [fieldControl]="control"
    [placeholder]="placeholder"
    [focus]="focusOnInput"
    [withPrefix]="true"
    [withSuffix]="true"
    [showLabel]="false"
    ariaRole="combobox"
    [ariaControls]="ariaControls"
    [ariaExpanded]="ariaExpanded"
    ariaAutocomplete="list"
    (clicked)="inputClicked.emit($event)"
    #textInput
  >
    <ods-icon prefix name="search" />
    @if (control.value) {
      <button
        suffix
        class="focus-visible:outline-focus rounded focus-visible:outline-2"
        data-test-id="clear-instant-search"
        (click)="clearInput($event)"
        (keydown.enter)="clearInput()"
        aria-label="Eingabe löschen"
      >
        <ods-icon name="close" />
      </button>
    }
  </ods-text-input>`,
})
export class SearchFieldComponent {
  @Input({ required: true }) label: string;
  @Input() placeholder: string = EMPTY_STRING;
  @Input() control: FormControl<string> = new FormControl(EMPTY_STRING);
  @Input() focusOnInput: boolean = false;
  @Input() showLabel: boolean = false;
  @Input() ariaControls: string;
  @Input() ariaExpanded: boolean = false;

  @Output() inputClicked: EventEmitter<MouseEvent> = new EventEmitter<MouseEvent>();
  @Output() searchQueryCleared: EventEmitter<MouseEvent> = new EventEmitter<MouseEvent>();

  @ViewChild('textInput') textInputRef: TextInputComponent;

  clearInput(e: Event): void {
    e.stopPropagation();
    this.control.setValue(EMPTY_STRING);
    this.textInputRef.focusInput();
    this.searchQueryCleared.emit();
  }
}
